import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OrderDetailModal from './OrderDetailModal';
import StatusBadge from './StatusBadge';
import { formatPhone, getWhatsAppLink } from '../utils/phoneUtils';
import { getCsrfToken } from '../utils/authUtils';
import { useToast } from './ui/Toast';

const API_URL = process.env.REACT_APP_API_URL;

const emptyProduct = {
  name: '',
  description: '',
  size: '',
  category: '',
  price: '',
  produto: 'customizable',
  imageFile: null,
  imagePreview: '',
  imageNome: '',
};

const CATEGORY_LABELS = {
  BOTTOM: 'Botton',
  KEYCHAIN: 'Chaveiro',
  POLAROID: 'Polaroid',
  STICK: 'Sticker',
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { push } = useToast();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Estados de paginação
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1
  });

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    startDate: '',
    endDate: ''
  });

  const [sortOrder, setSortOrder] = useState('desc');
  const [productList, setProductList] = useState([]);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);

  useEffect(() => {
    fetchOrders();
    fetchProductList();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [orders, filters, sortOrder]);

  const fetchOrders = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/?page=${page}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.status === 401) {
        navigate('/admin/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Erro ao carregar pedidos');
      }

      const data = await response.json();

      if (data.results && Array.isArray(data.results)) {
        setOrders(data.results);
        setPagination({
          count: data.count || 0,
          next: data.next,
          previous: data.previous,
          currentPage: page
        });
      } else {
        // Fallback para array direto (sem paginação)
        setOrders(Array.isArray(data) ? data : []);
        setPagination({
          count: Array.isArray(data) ? data.length : 0,
          next: null,
          previous: null,
          currentPage: 1
        });
      }
    } catch (err) {
      setError('Erro ao carregar pedidos: ' + err.message);
      console.error('Erro:', err);
      setOrders([]);
      setPagination({ count: 0, next: null, previous: null, currentPage: 1 });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    // Garantir que orders seja um array antes de fazer operações
    if (!Array.isArray(orders)) {
      setFilteredOrders([]);
      return;
    }

    let filtered = [...orders];

    // Filtro por busca (nome, email, telefone ou ID)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(order =>
        (order.phone_number && order.phone_number.includes(filters.search)) ||
        order.id.toString().includes(searchLower)
      );
    }

    // Filtro por status (API usa status em MAIÚSCULAS)
    if (filters.status !== 'all') {
      filtered = filtered.filter(order =>
        order.status && order.status.toUpperCase() === filters.status.toUpperCase()
      );
    }

    // Filtro por data de início
    if (filters.startDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        const startDate = new Date(filters.startDate);
        return orderDate >= startDate;
      });
    }

    // Filtro por data de fim
    if (filters.endDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999); // Final do dia
        return orderDate <= endDate;
      });
    }

    // Ordenação por data
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    setFilteredOrders(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      startDate: '',
      endDate: ''
    });
  };

  const fetchProductList = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products/?all=1`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      setProductList(data.results || data);
    } catch {
      push('Não foi possível carregar o catálogo de produtos.', 'danger');
    }
  };

  const openProductForm = (product = null) => {
    if (product) {
      setEditingProductId(product.id);
      setProductForm({
        name: product.name,
        description: product.description,
        size: product.size || '',
        category: product.category || '',
        price: product.price,
        produto: product.is_customizable ? 'customizable' : 'available',
        imageFile: null,
        imagePreview: product.image || '',
        imageNome: '',
      });
    } else {
      setEditingProductId(null);
      setProductForm(emptyProduct);
    }
    setIsProductFormOpen(true);
  };

  const handleProductImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      setProductForm((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: reader.result,
        imageNome: file.name,
      }));
    reader.readAsDataURL(file);
  };

  const saveProduct = async (event) => {
    event.preventDefault();
    const isEdit = Boolean(editingProductId);

    const body = new FormData();
    body.append('name', productForm.name);
    body.append('description', productForm.description);
    body.append('size', productForm.size);
    body.append('category', productForm.category);
    if (productForm.produto === 'customizable') {
      body.append('price', String(productForm.price || 0));
    }
    body.append('is_customizable', productForm.produto === 'customizable' ? 'true' : 'false');
    if (productForm.imageFile) {
      body.append('image', productForm.imageFile);
    }

    try {
      const response = await fetch(
        isEdit ? `${API_URL}/api/products/${editingProductId}/` : `${API_URL}/api/products/`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          credentials: 'include',
          headers: { 'X-CSRFToken': getCsrfToken() },
          body,
        }
      );

      if (!response.ok) {
        throw new Error(
          response.status === 400
            ? 'Verifique os campos obrigatórios (incluindo categoria e preço).'
            : 'Não foi possível salvar o produto.'
        );
      }

      setIsProductFormOpen(false);
      setEditingProductId(null);
      setProductForm(emptyProduct);
      push(isEdit ? 'Produto atualizado.' : 'Produto criado.', 'success');
      fetchProductList();
    } catch (err) {
      push(err.message, 'danger');
    }
  };

  const deleteProduct = async () => {
    const { id, name } = productToDelete;
    setProductToDelete(null);

    try {
      const response = await fetch(`${API_URL}/api/products/${id}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-CSRFToken': getCsrfToken() },
      });

      if (response.status === 409) {
        push(`"${name}" está em pedidos existentes — desative-o em vez de excluir.`, 'danger');
        return;
      }
      if (!response.ok) throw new Error();

      push('Produto excluído.', 'success');
      fetchProductList();
    } catch {
      push('Não foi possível excluir o produto.', 'danger');
    }
  };

  const toggleActive = async (product) => {
    try {
      const response = await fetch(`${API_URL}/api/products/${product.id}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken() },
        body: JSON.stringify({ is_active: !product.is_active }),
      });
      if (!response.ok) throw new Error();
      push(product.is_active ? 'Produto desativado.' : 'Produto ativado.', 'success');
      fetchProductList();
    } catch {
      push('Não foi possível alterar o status do produto.', 'danger');
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/${orderId}/`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar detalhes do pedido');
      }

      const data = await response.json();
      setSelectedOrder(data);
      setIsModalOpen(true);
    } catch (err) {
      push('Não foi possível carregar os detalhes do pedido.', 'danger');
      console.error('Erro:', err);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders/${orderId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({ status: newStatus.toUpperCase() })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar status');
      }

      // Atualizar a lista local
      setOrders(prev => prev.map(order =>
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      // Se o modal está aberto com este pedido, atualizar também
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
          <p className="text-ink-soft">Carregando pedidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-cream-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-ink">Arttibus</h1>
              <p className="text-ink-soft mt-1">Painel de Gestão - Arttibus</p>
            </div>
            <div className="admin-header-actions"><button onClick={() => navigate('/')} className="admin-outline-button">Voltar para a Loja</button><button onClick={fetchOrders} className="admin-primary-button">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Atualizar
            </button><button onClick={() => navigate('/admin/login')} className="admin-outline-button">Sair</button></div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <section className="product-admin-section">
          <div className="product-admin-heading"><div><p className="admin-kicker">catálogo</p><h2>Gestão de Produtos</h2><p>Gerencie o catálogo de produtos disponíveis e personalizáveis</p></div><button onClick={() => openProductForm()} className="admin-primary-button">+ Criar Novo Produto</button></div>
          <div className="admin-summary-grid"><div><span>Total de Produtos</span><strong>{productList.length}</strong></div><div><span>Personalizáveis</span><strong>{productList.filter(product => product.is_customizable).length}</strong></div><div><span>Ativos na loja</span><strong>{productList.filter(product => product.is_active).length}</strong></div></div>
          <div className="product-admin-table-wrap"><table className="product-admin-table"><thead><tr><th>Imagem</th><th>Produto</th><th>Tamanho</th><th>Categoria</th><th>Preço</th><th>Status</th><th>Ações</th></tr></thead><tbody>{productList.map(product => <tr key={product.id}><td><div className="product-admin-thumb">{product.image ? <img src={product.image} alt={product.name} /> : <span>{(product.name || 'A').charAt(0)}</span>}</div></td><td><strong>{product.name}</strong><small>{product.description}</small></td><td>{product.size || '—'}</td><td>{CATEGORY_LABELS[product.category] || '—'}</td><td><strong>{product.is_customizable ? `R$ ${Number(product.price).toFixed(2)}` : (product.initial_price ? `a partir de R$ ${Number(product.initial_price).toFixed(2)}` : '—')}</strong></td><td><button onClick={() => toggleActive(product)} className={product.is_active ? 'admin-type-custom' : 'admin-type-ready'} aria-label={product.is_active ? `Desativar ${product.name}` : `Ativar ${product.name}`}>{product.is_active ? 'Ativo' : 'Inativo'}</button></td><td><div className="admin-row-actions"><button onClick={() => openProductForm(product)} className="admin-edit-button">Editar</button><button onClick={() => setProductToDelete(product)} className="admin-delete-button">Excluir</button></div></td></tr>)}</tbody></table></div>
        </section>
        {isProductFormOpen && <div className="admin-modal-backdrop"><form className="product-form-modal" onSubmit={saveProduct}><div className="admin-modal-header"><div><p className="admin-kicker">{editingProductId ? 'editar produto' : 'novo produto'}</p><h2>{editingProductId ? 'Editar Produto' : 'Criar Novo Produto'}</h2></div><button type="button" className="admin-close-button" onClick={() => setIsProductFormOpen(false)} aria-label="Fechar">×</button></div><div className="product-form-grid"><label>Título do Produto<input id="titulo" type="text" placeholder="Ex: Button Redondo 5,8cm" value={productForm.name} onChange={event => setProductForm({ ...productForm, name: event.target.value })} required /></label><label>Categoria<select id="categoria" value={productForm.category} onChange={event => setProductForm({ ...productForm, category: event.target.value })} required style={{ background: 'var(--input)', border: '0', borderRadius: '12px', color: 'var(--text)', display: 'block', font: 'inherit', fontSize: '12px', marginTop: '8px', outline: '0', padding: '12px', width: '100%' }}><option value="">Selecione…</option><option value="BOTTOM">Botton</option><option value="KEYCHAIN">Chaveiro</option><option value="POLAROID">Polaroid</option><option value="STICK">Sticker</option></select></label><label>Tamanho<select id="tamanho" value={productForm.size} onChange={event => setProductForm({ ...productForm, size: event.target.value })} required style={{ background: 'var(--input)', border: '0', borderRadius: '12px', color: 'var(--text)', display: 'block', font: 'inherit', fontSize: '12px', marginTop: '8px', outline: '0', padding: '12px', width: '100%' }}><option value="">Selecione…</option><option value="2.5">2,5 cm</option><option value="3.7">3,7 cm</option><option value="5.8">5,8 cm</option></select></label>{productForm.produto === 'customizable' && <label>Preço (R$)<input id="preco" type="number" min="0" step="0.01" placeholder="Ex: 6.00" value={productForm.price} onChange={event => setProductForm({ ...productForm, price: event.target.value })} required /></label>}<label className="product-form-full">Descrição<textarea id="descricao" placeholder="Descreva o produto com detalhes..." value={productForm.description} onChange={event => setProductForm({ ...productForm, description: event.target.value })} required /></label><fieldset className="product-form-full"><legend>Tipo de Produto</legend><label className="radio-option"><input id="produto" type="radio" name="produto" value="customizable" checked={productForm.produto === 'customizable'} onChange={event => setProductForm({ ...productForm, produto: event.target.value })} /> Produto Personalizável <small>(Exige upload do cliente na loja)</small></label><label className="radio-option"><input type="radio" name="produto" value="available" checked={productForm.produto === 'available'} onChange={event => setProductForm({ ...productForm, produto: event.target.value })} /> Produto Disponível / Predefinido <small>(Pronta entrega)</small></label></fieldset><label className="product-form-full">Imagem do Produto<input id="imagem" type="file" accept="image/*" onChange={handleProductImage} required={!editingProductId} /><span className="upload-preview">{productForm.imagePreview ? <img src={productForm.imagePreview} alt="Prévia do produto" /> : 'Selecione uma imagem para ver a prévia'}{productForm.imageNome && <small>{productForm.imageNome}</small>}</span></label></div><div className="admin-modal-actions"><button type="button" className="admin-outline-button" onClick={() => setIsProductFormOpen(false)}>Cancelar</button><button type="submit" className="admin-primary-button">Salvar Produto</button></div></form></div>}
        {productToDelete && <div className="admin-modal-backdrop"><div className="delete-confirm-modal"><p className="admin-kicker">ação permanente</p><h2>Tem certeza que deseja excluir este produto?</h2><p>{productToDelete.name}</p><div className="admin-modal-actions"><button className="admin-outline-button" onClick={() => setProductToDelete(null)}>Cancelar</button><button className="admin-delete-button delete-confirm-button" onClick={deleteProduct}>Excluir</button></div></div></div>}
        {error && (
          <div className="rounded-xl bg-clay-50 border border-clay-200 text-clay-700 px-4 py-3 mb-6">
            {error}
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-cream-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-ink mb-4">Filtros</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Busca */}
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Nome, email ou ID..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">Todos</option>
                <option value="pending">Pendente</option>
                <option value="paid">Pago</option>
                <option value="processing">Em Processamento</option>
                <option value="ready">Pronto</option>
                <option value="delivered">Entregue</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </div>

            {/* Data Início */}
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-2">
                Data Início
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Data Fim */}
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-2">
                Data Fim
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-cream-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-cream-200">
            <button
              onClick={clearFilters}
              className="text-sm text-ink-soft hover:text-ink font-medium"
            >
              Limpar Filtros
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-soft">Ordenar por data:</span>
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="flex items-center gap-1 px-3 py-1 bg-cream-100 hover:bg-cream-200 rounded-lg text-sm font-medium transition-colors"
              >
                {sortOrder === 'desc' ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                    Mais recente
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                    </svg>
                    Mais antigo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-cream-200 p-4">
            <div className="text-sm text-ink-soft">Total de Pedidos</div>
            <div className="text-2xl font-bold text-ink mt-1">{filteredOrders.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-cream-200 p-4">
            <div className="text-sm text-ink-soft">Valor Total Vendido</div>
            <div className="text-2xl font-bold text-sage-600 mt-1">
              {formatCurrency(filteredOrders.reduce((sum, order) => sum + parseFloat(order.total_price || order.total_amount || 0), 0))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-cream-200 p-4">
            <div className="text-sm text-ink-soft">Pendentes</div>
            <div className="text-2xl font-bold text-caramel-600 mt-1">
              {filteredOrders.filter(o => o.status && o.status.toUpperCase() === 'PENDING').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-cream-200 p-4">
            <div className="text-sm text-ink-soft">Em Processamento</div>
            <div className="text-2xl font-bold text-cocoa-700 mt-1">
              {filteredOrders.filter(o => o.status && o.status.toUpperCase() === 'PROCESSING').length}
            </div>
          </div>
        </div>

        {/* Tabela de Pedidos */}
        <div className="bg-white rounded-lg shadow-sm border border-cream-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-cream-200">
              <thead className="bg-cream-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-cream-200">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-ink-muted">
                      <svg className="w-16 h-16 mx-auto text-cream-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                      <p className="text-lg font-medium">Nenhum pedido encontrado</p>
                      <p className="text-sm mt-1">Tente ajustar os filtros ou aguarde novos pedidos</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-cream-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink">
                        #{order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-ink">
                            {formatPhone(order.phone_number) || 'N/A'}
                          </div>
                          {order.phone_number && (
                            <a
                              href={getWhatsAppLink(order.phone_number)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sage-600 hover:text-sage-700 transition-colors"
                              title="Abrir WhatsApp"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ink">
                        {formatCurrency(order.total_price || order.total_amount || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={order.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleViewOrder(order.id)}
                          className="text-primary-600 hover:text-primary-900 font-medium"
                        >
                          Ver Detalhes
                        </button>
                        <div className="relative inline-block text-left">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                handleStatusChange(order.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="text-secondary-700 hover:text-secondary-900 font-medium bg-transparent border-none cursor-pointer focus:ring-0"
                            defaultValue=""
                          >
                            <option value="" disabled>Alterar Status</option>
                            <option value="paid">Pago</option>
                            <option value="processing">Em produção</option>
                            <option value="ready">Pronto</option>
                            <option value="delivered">Entregue</option>
                            <option value="cancelled">Cancelado</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Controles de Paginação */}
          {(pagination.next || pagination.previous) && (
            <div className="bg-cream-50 px-6 py-4 border-t border-cream-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-ink-soft">
                  Página {pagination.currentPage} - Mostrando {orders.length} de {pagination.count} pedidos
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchOrders(pagination.currentPage - 1)}
                    disabled={!pagination.previous}
                    className="px-4 py-2 bg-white border border-cream-300 rounded-lg text-sm font-medium text-ink-soft hover:bg-cream-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Anterior
                  </button>
                  <button
                    onClick={() => fetchOrders(pagination.currentPage + 1)}
                    disabled={!pagination.next}
                    className="px-4 py-2 bg-white border border-cream-300 rounded-lg text-sm font-medium text-ink-soft hover:bg-cream-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    Próxima
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modal de Detalhes */}
      {isModalOpen && selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedOrder(null);
          }}
          onStatusChange={handleStatusChange}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
